import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  GridItem,
  HStack,
  Menu,
  MenuButton,
  Portal,
  Spacer,
  Tag,
  TagCloseButton,
  TagLabel,
  useColorModeValue,
} from '@chakra-ui/react';
import { useState } from 'react';
import { sum } from 'lodash';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as SparkelCopilotIcon } from '../../../assets/icons/sparkel-copilot.svg';
import { ViewerMode } from 'src/components/common/viewer-mode';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';
import { AutoshapePopover } from 'src/components/orderContractor/AutoshapePopover';
import MeasureToolButton from 'src/components/viewers/SheetsViewerV2/components/MeasureToolButton';
import { Pagination } from 'src/components/viewers/SheetsViewerV2/components/Pagination';
import { PDFOpacitySlider } from 'src/components/viewers/SheetsViewerV2/components/PDFOpacitySlider';
import { SheetCalibration } from 'src/components/viewers/SheetsViewerV2/components/SheetCalibration';
import SheetShapeLabelsButton from 'src/components/viewers/SheetsViewerV2/components/SheetShapeLabelsButton';
import SnappingButton from 'src/components/viewers/SheetsViewerV2/components/SnappingButton';
import WindLoadZonesToggleButton from 'src/components/viewers/SheetsViewerV2/components/WindLoadZonesToggleButton';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import { AutoShapeStatus } from 'src/gql/graphql';
import { useAutoShapes } from 'src/hooks/auto-shapes';
import { useUserTenant } from 'src/services/auth-info';

export function SheetsViewerV2Interactions({
  readOnly,
}: {
  readOnly?: boolean;
}) {
  const { projectId } = useParams<{ projectId: string }>();
  const {
    viewerMode,
    pagination,
    activeSheet,
    sheets,
    visibilityManager: {
      setIsolatedElements,
      setHiddenElements,
      hiddenElements,
      isolatedElements,
    },
  } = useSheetViewerV2();
  const { t } = useTranslation('project');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const numHiddenElements = sum(
    Object.values(hiddenElements).map((v) => v.length)
  );
  const numIsolatedElements = sum(
    Object.values(isolatedElements).map((v) => v.length)
  );

  const { isProtan, isFreeContractor } = useUserTenant();

  // Auto-shapes hook for free contractors AI button
  const { autoShapes, createAutoShape, findNextAvailableAutoShapeName } =
    useAutoShapes();

  // Check if any autoshape is currently running (processing or pending)
  const hasProcessingAutoShapes = autoShapes?.some(
    (shape) =>
      shape.status === AutoShapeStatus.Processing ||
      shape.status === AutoShapeStatus.Pending
  );

  const [preventClose, setPreventClose] = useState(false);

  if (
    viewerMode !== ViewerMode.SheetsV2 ||
    activeSheet?.isLoading ||
    !sheets ||
    sheets.length === 0
  ) {
    return null;
  }

  return (
    <GridItem
      area="viewer-interactions"
      display="flex"
      pointerEvents="none"
      paddingTop={2}
      zIndex={999}
      position="relative"
      sx={{
        '> *:not(:empty)': {
          pointerEvents: 'auto',
        },
        '@media (max-width: 1200px)': {
          paddingRight: 2,
        },
      }}
    >
      <Pagination {...pagination} />
      <ButtonGroup mr={2}>
        {!readOnly && <SheetCalibration />}
        <Divider borderColor={borderColor} orientation="vertical" />
        <PDFOpacitySlider />
        <SheetShapeLabelsButton />
        {isProtan && <WindLoadZonesToggleButton />}
        <Divider borderColor={borderColor} orientation="vertical" />
        <SnappingButton />
        <Divider borderColor={borderColor} orientation="vertical" />
        <MeasureToolButton />
      </ButtonGroup>
      {isFreeContractor && (
        <Box
          position="absolute"
          left="50%"
          transform="translateX(-50%)"
          pointerEvents="auto"
        >
          <Menu isLazy closeOnBlur={!preventClose}>
            {({ onClose }) => (
              <>
                <MenuButton
                  as={Button}
                  leftIcon={
                    <SparkelIcon as={SparkelCopilotIcon} color="inherit" />
                  }
                  size="xs"
                  colorScheme="purple"
                  variant="sparkel-ai"
                  isLoading={hasProcessingAutoShapes}
                >
                  Autoshapes
                </MenuButton>
                <Portal>
                  <AutoshapePopover
                    viewerMode={viewerMode}
                    createAutoShape={createAutoShape}
                    onClose={onClose}
                    findNextAvailableAutoShapeName={
                      findNextAvailableAutoShapeName
                    }
                    modelId=""
                    modelUrn=""
                    globalOffset={{ x: 0, y: 0, z: 0 }}
                    sheetId={activeSheet?.data?.id ?? ''}
                    pageNumber={activeSheet?.data?.page ?? 1}
                    templateRect={{ x: 100, y: 100, width: 50, height: 50 }}
                    sensitivity={0.5}
                    enableAutoShapes={true}
                    projectId={projectId}
                    onPreventCloseChange={setPreventClose}
                  />
                </Portal>
              </>
            )}
          </Menu>
        </Box>
      )}
      <Spacer />
      <HStack spacing={2}>
        {numIsolatedElements > 0 ? (
          <Tag size="sm" borderRadius="full" variant="toggle">
            <TagLabel>
              {t('viewer-interactions.isolated-elements', {
                numIsolatedElements,
              })}
            </TagLabel>
            <TagCloseButton
              onClick={() => setIsolatedElements({})}
              aria-label={t('viewer-interactions.clear-isolated')}
            />
          </Tag>
        ) : null}
        {numHiddenElements > 0 ? (
          <Tag size="sm" borderRadius="full" variant="toggle">
            <TagLabel>
              {t('viewer-interactions.hidden-elements', { numHiddenElements })}
            </TagLabel>
            <TagCloseButton
              onClick={() => setHiddenElements({})}
              aria-label={t('viewer-interactions.clear-hidden')}
            />
          </Tag>
        ) : null}
      </HStack>
    </GridItem>
  );
}
